import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { CircularProgress, Typography } from '@mui/material';
import BackpackRounded from '@mui/icons-material/BackpackRounded';
import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import RouteRounded from '@mui/icons-material/RouteRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import FactList from 'components/common/FactList/FactList';
import Notice from 'components/common/Notice/Notice';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import TSCard from 'components/common/TSCard/TSCard';
import WeatherForecast from 'components/TrailSafe/WeatherForecast/WeatherForecast';
import { useGetSharedPlanQuery } from 'app/api/sharedPlanApiSlice';
import { formatTrailDateTime, trailToday } from 'utils/trailTime';

// Checked again every minute, so the live status stays current on an open
// page.
const POLL_MS = 60 * 1000;

// Same guidance-only stance as the Welfare Check response pages: Trail Safe
// never dispatches rescue itself (docs/adr/0001).
const EMERGENCY_ADVICE =
  'If you believe this is a genuine emergency, contact emergency services (000) or Tasmania Police directly — Trail Safe does not dispatch rescue services automatically.';

const GEAR_ANSWERS = {
  TAKING: [CheckRounded, 'Taking'],
  NOT_TAKING: [CloseRounded, 'Not taking'],
  UNANSWERED: [HelpOutlineRounded, 'Not answered'],
};

// The badge, notice tone and message for each live status. Copy is
// hardcoded with interpolation (docs/adr/0005).
const statusOf = (plan, today) => {
  const who = plan.walker_name;
  const back = formatTrailDateTime(plan.expected_return);
  switch (plan.status) {
    case 'WALKING':
      return [
        'onTrack',
        'Walking',
        'success',
        `${who} started at ${formatTrailDateTime(plan.started_at)} and expects to be back by ${back}.`,
      ];
    case 'OVERDUE':
      return [
        'overdue',
        'Overdue',
        'danger',
        `${who} was expected back by ${back} and hasn't checked in. A Welfare Check is in progress: Trail Safe is contacting them, then their Emergency Contact. ${EMERGENCY_ADVICE}`,
        'Overdue: Welfare Check in progress',
      ];
    case 'FINISHED':
      return [
        'finished',
        'Finished',
        'success',
        `${who} finished at ${formatTrailDateTime(plan.finished_at)}.`,
      ];
    case 'CANCELLED':
      return ['cancelled', 'Cancelled', 'info', `${who} cancelled this Plan.`];
    case 'MISSED':
      return [
        'missed',
        'Missed',
        'info',
        `${who} didn't start this Plan on its day.`,
      ];
    default:
      return [
        plan.planned_date === today ? 'today' : 'planned',
        'Planned',
        'info',
        `${who} hasn't started yet.`,
      ];
  }
};

// The Shared Plan page: a Plan and its live status for anyone the walker
// sends the link to, such as their Emergency Contact and Plan Guests. It
// never shows guests' phone numbers. Minimal layout, like the Welfare Check
// response pages (docs/adr/0006).
const SharedPlan = () => {
  const { token } = useParams();
  const {
    data: plan,
    isLoading,
    isError,
  } = useGetSharedPlanQuery(token, { pollingInterval: POLL_MS });

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !plan) {
    return (
      <div className="flex flex-col gap-2">
        <Typography variant="h5" fontWeight={700}>
          This link isn't working
        </Typography>
        <Typography variant="body1">
          Shared Plan links stop working a day after the walk ends. If you were
          sent this one recently, check it was copied in full.
        </Typography>
      </div>
    );
  }

  const today = trailToday();
  const [badge, label, tone, message, title] = statusOf(plan, today);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3">
        <div>
          <StatusBadge status={badge} label={label} />
        </div>
        <Typography variant="h2">
          {plan.walker_name}'s walk: {plan.trail_name}, {plan.park_name}
        </Typography>
        <Notice tone={tone} title={title}>
          {message}
        </Notice>
      </header>
      <TSCard>
        <FactList>
          <li>
            <CalendarTodayRounded aria-hidden="true" />
            <span>
              {plan.planned_date === today
                ? 'Today'
                : dayjs(plan.planned_date).format('ddd D MMM YYYY')}
              , starting about {plan.planned_start_time}
            </span>
          </li>
          <li>
            <RouteRounded aria-hidden="true" />
            <span>
              {plan.start_location}: {plan.trail_option_name}
            </span>
          </li>
          {plan.started_at && (
            <li>
              <FlagRounded aria-hidden="true" />
              <span>Started {formatTrailDateTime(plan.started_at)}</span>
            </li>
          )}
          <li>
            <ScheduleRounded aria-hidden="true" />
            <span>
              Expected back by {formatTrailDateTime(plan.expected_return)}
            </span>
          </li>
          {plan.finished_at && (
            <li>
              <FlagRounded aria-hidden="true" />
              <span>Finished {formatTrailDateTime(plan.finished_at)}</span>
            </li>
          )}
        </FactList>
      </TSCard>
      <TSCard icon={<GroupsRounded />} title="Walking with">
        {plan.guests.length > 0 ? (
          <FactList>
            {plan.guests.map((guest, i) => (
              <li key={i}>
                <span>{guest.name}</span>
              </li>
            ))}
          </FactList>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No Plan Guests.
          </Typography>
        )}
      </TSCard>
      {plan.notes && (
        <TSCard icon={<EditNoteRounded />} title="Notes">
          <Typography variant="body1" className="whitespace-pre-wrap">
            {plan.notes}
          </Typography>
        </TSCard>
      )}
      <TSCard icon={<BackpackRounded />} title="Gear List">
        <FactList>
          {plan.gear_list.map((item, i) => {
            const [Icon, answer] = GEAR_ANSWERS[item.status];
            return (
              <li key={i}>
                <Icon aria-hidden="true" />
                <span>
                  {item.name}
                  {item.essential && <span className="ts-tag">Essential</span>}:{' '}
                  {answer}
                </span>
              </li>
            );
          })}
        </FactList>
      </TSCard>
      <WeatherForecast trailId={plan.trail_id} />
    </div>
  );
};

export default SharedPlan;
